import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Printer, Warning, ShieldCheck } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Public, unauthenticated view of a recipe's spec sheet. Loaded from a
 * share token the owner generates in the Recipe Builder. Designed to:
 *   • Look credible enough to forward to a co-packer / buyer / investor.
 *   • Carry an "Alaine's Bench" footer back-link so every share doubles
 *     as a top-of-funnel acquisition surface (the viral loop).
 *
 * Intentionally omits cost / margin data — owners forward to outside
 * parties and we don't want to leak commercial-sensitive info.
 */
export default function PublicSpecSheet() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get(`${API}/public/spec-sheet/${token}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "This link is no longer valid."));
  }, [token]);

  // Inject Open Graph + Twitter Card meta tags so LinkedIn / Slack / iMessage
  // unfurls show the dynamic spec-sheet preview card instead of a generic
  // app icon. Crawlers that don't execute JS (Twitter/X, Facebook) still
  // need static HTML — we'll add a server-rendered HTML route next iteration
  // if those become a primary distribution channel. LinkedIn/Slack handle
  // JS-injected meta tags fine today.
  useEffect(() => {
    if (!data) return;
    const r = data.recipe || {};
    const bp = data.business_profile || {};
    const cal = data?.nutrition?.per_serving?.calories;
    const sodium = data?.nutrition?.per_serving?.sodium_mg;
    const title = `${r.product_name || r.name || "Spec Sheet"} — ${bp.brand_name || bp.legal_entity || "Spec Sheet"}`;
    const description = `Spec sheet built on FDA labeling rules · ${cal ?? "—"} cal · ${sodium ?? "—"}mg sodium per ${r.serving_size_g || ""}g serving. Made with Alaine's Bench™.`;
    const ogImage = `${API}/public/spec-sheet/${token}/og-image.png`;
    const pageUrl = `${window.location.origin}/spec/${token}`;

    document.title = title;
    setMeta("description", description);
    setMeta("og:title", title, true);
    setMeta("og:description", description, true);
    setMeta("og:image", ogImage, true);
    setMeta("og:image:width", "1200", true);
    setMeta("og:image:height", "630", true);
    setMeta("og:type", "article", true);
    setMeta("og:url", pageUrl, true);
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", title);
    setMeta("twitter:description", description);
    setMeta("twitter:image", ogImage);
  }, [data, token]);

  if (error) {
    return (
      <div className="min-h-screen grid place-items-center bg-stone-50 p-6">
        <div className="max-w-md text-center bg-white border border-stone-200 rounded-lg p-8">
          <Warning size={28} weight="duotone" className="text-amber-600 mx-auto" />
          <h1 className="mt-3 text-xl font-semibold text-stone-900">Link unavailable</h1>
          <p className="mt-2 text-sm text-stone-600">{error}</p>
          <Link to="/" className="mt-5 inline-block text-sm text-emerald-800 hover:underline" data-testid="public-spec-home-link">
            Visit Alaine's Bench →
          </Link>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen grid place-items-center bg-stone-50 text-stone-500" data-testid="public-spec-loading">
        Loading spec sheet…
      </div>
    );
  }

  const { recipe: r, business_profile: bp, nutrition: n, statement, ingredient_table, salt, total_batch_g } = data;
  const ps = n.per_serving;
  const dv = n.daily_values;

  return (
    <div className="min-h-screen bg-stone-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 print:py-2" data-testid="public-spec-sheet">
        {/* Brand header (owner's, not Alaine's Bench's) */}
        <header className="bg-white border border-stone-200 rounded-t-lg px-6 py-5 flex items-start justify-between print:rounded-none">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-stone-500 font-bold">Product Spec Sheet</div>
            <div className="mt-2 font-bold text-2xl text-stone-900" data-testid="public-spec-brand">
              {bp?.brand_name || bp?.legal_entity || "Brand-confidential"}
            </div>
            {bp?.address_line1 && (
              <div className="text-xs text-stone-500 mt-1 leading-snug">
                {bp.address_line1}
                {bp.address_line2 ? `, ${bp.address_line2}` : ""}
                {bp.city ? ` · ${bp.city}` : ""}
                {bp.state ? `, ${bp.state}` : ""}
                {bp.zip_code ? ` ${bp.zip_code}` : ""}
              </div>
            )}
            {bp?.website && (
              <div className="text-xs text-emerald-800 mt-0.5">{bp.website}</div>
            )}
          </div>
          <button
            type="button"
            onClick={() => window.print()}
            className="text-xs text-stone-600 hover:text-emerald-800 inline-flex items-center gap-1 print:hidden"
            data-testid="public-spec-print-button"
          >
            <Printer size={14} /> Print / PDF
          </button>
        </header>

        {/* Product identity */}
        <section className="bg-white border-x border-b border-stone-200 px-6 py-5">
          <h1 className="text-3xl font-bold tracking-tight text-stone-900" data-testid="public-spec-product-name">
            {r.product_name || r.name}
          </h1>
          <div className="mt-2 flex flex-wrap gap-2 text-[11px] uppercase tracking-wider font-bold">
            {r.sku && <span className="font-mono bg-stone-100 text-stone-700 px-1.5 py-0.5 rounded">SKU: {r.sku}</span>}
            <span className="bg-stone-100 text-stone-700 px-1.5 py-0.5 rounded">{r.version || "v1"}</span>
            {r.status && <span className="bg-blue-100 text-blue-800 px-1.5 py-0.5 rounded">{r.status}</span>}
            {r.product_type && (
              <span className="bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded">
                {r.product_type.replace(/_/g, " ")}
              </span>
            )}
          </div>
          {r.description && <p className="mt-3 text-sm text-stone-700 leading-relaxed">{r.description}</p>}
        </section>

        {/* Two columns: nutrition panel + ingredient statement */}
        <section className="grid md:grid-cols-2 gap-0 bg-white border-x border-b border-stone-200">
          {/* Nutrition Facts */}
          <div className="p-6 border-r border-stone-200" data-testid="public-spec-nutrition">
            <div className="text-xs uppercase tracking-[0.2em] text-stone-500 font-bold mb-3">Nutrition Facts</div>
            <div className="bg-white border-2 border-stone-900 rounded p-3 font-sans" style={{ fontFamily: "Helvetica, Arial, sans-serif" }}>
              <div className="text-[10px] text-stone-700">Serving size {r.serving_size_g}g</div>
              <div className="text-[10px] text-stone-700">Servings per container ~{r.servings_per_container}</div>
              <div className="border-t-8 border-stone-900 my-2" />
              <div className="flex justify-between items-end">
                <div className="text-[10px] font-bold">Amount per serving</div>
              </div>
              <div className="flex justify-between font-bold text-[22px] leading-tight">
                <span>Calories</span><span>{ps.calories}</span>
              </div>
              <div className="border-t-4 border-stone-900 my-1" />
              <div className="text-[10px] text-right font-bold">% Daily Value*</div>
              <Row label="Total Fat" value={`${ps.total_fat_g}g`} dv={dv.total_fat_g} bold />
              <Row label="Saturated Fat" value={`${ps.saturated_fat_g}g`} dv={dv.saturated_fat_g} indent />
              <Row label="Trans Fat" value={`${ps.trans_fat_g}g`} indent italic />
              <Row label="Cholesterol" value={`${ps.cholesterol_mg}mg`} dv={dv.cholesterol_mg} bold />
              <Row label="Sodium" value={`${ps.sodium_mg}mg`} dv={dv.sodium_mg} bold />
              <Row label="Total Carbohydrate" value={`${ps.total_carbs_g}g`} dv={dv.total_carbs_g} bold />
              <Row label="Dietary Fiber" value={`${ps.fiber_g}g`} dv={dv.fiber_g} indent />
              <Row label="Total Sugars" value={`${ps.sugars_g}g`} indent />
              <Row label="Incl. Added Sugars" value={`${ps.added_sugars_g}g`} dv={dv.added_sugars_g} indent2 />
              <Row label="Protein" value={`${ps.protein_g}g`} bold />
              <div className="border-t-4 border-stone-900 my-1" />
              <Row label="Vitamin D" value={`${ps.vitamin_d_mcg}mcg`} dv={dv.vitamin_d_mcg} />
              <Row label="Calcium" value={`${ps.calcium_mg}mg`} dv={dv.calcium_mg} />
              <Row label="Iron" value={`${ps.iron_mg}mg`} dv={dv.iron_mg} />
              <Row label="Potassium" value={`${ps.potassium_mg}mg`} dv={dv.potassium_mg} />
              <div className="border-t border-stone-900 my-1" />
              <div className="text-[8px] text-stone-600 leading-snug">
                *Percent Daily Values are based on a 2,000 calorie diet.
              </div>
            </div>
          </div>

          {/* Ingredient statement + allergens */}
          <div className="p-6" data-testid="public-spec-statement">
            <div className="text-xs uppercase tracking-[0.2em] text-stone-500 font-bold mb-3">Ingredient Statement</div>
            <p className="text-[13px] text-stone-900 leading-relaxed">{statement.statement}</p>
            {statement.contains_line && (
              <p className="mt-4 text-[13px] font-bold text-stone-900" data-testid="public-spec-contains">
                {statement.contains_line}
              </p>
            )}
            {statement.may_contain_line && (
              <p className="mt-2 text-[13px] text-stone-700 italic">{statement.may_contain_line}</p>
            )}
            {salt && (
              <div className="mt-5 bg-stone-50 border border-stone-200 rounded p-3 text-xs text-stone-700">
                <div className="font-semibold text-stone-800">Salt content</div>
                <div className="mt-1">
                  {salt.pct}% w/w · tolerance band {salt.band_label}
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Formula breakdown — % only, no costs */}
        <section className="bg-white border-x border-b border-stone-200 px-6 py-5">
          <div className="text-xs uppercase tracking-[0.2em] text-stone-500 font-bold mb-3">Formula breakdown</div>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-stone-200 text-[11px] uppercase tracking-wider text-stone-500">
                <th className="text-left py-2">Ingredient</th>
                <th className="text-right py-2 w-24">%</th>
                <th className="text-right py-2 w-24">Per {total_batch_g.toFixed(0)}g</th>
              </tr>
            </thead>
            <tbody>
              {ingredient_table.map((row, i) => (
                <tr key={i} className="border-b border-stone-100">
                  <td className="py-1.5">{row.name}</td>
                  <td className="py-1.5 text-right font-mono">{row.percent}%</td>
                  <td className="py-1.5 text-right font-mono text-stone-600">{row.grams.toFixed(1)}g</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        {/* Spec block */}
        {(r.mesh_size || r.shelf_life_months || r.storage_conditions || r.packaging || r.micro_spec) && (
          <section className="bg-white border-x border-b border-stone-200 px-6 py-5">
            <div className="text-xs uppercase tracking-[0.2em] text-stone-500 font-bold mb-3">Specifications</div>
            <dl className="grid sm:grid-cols-2 gap-x-8 gap-y-2 text-sm">
              {r.mesh_size && <Spec k="Mesh / particle" v={r.mesh_size} />}
              {r.shelf_life_months && <Spec k="Shelf life" v={`${r.shelf_life_months} months`} />}
              {r.storage_conditions && <Spec k="Storage" v={r.storage_conditions} />}
              {r.packaging && <Spec k="Packaging" v={r.packaging} />}
              {r.micro_spec && <Spec k="Microbiological" v={r.micro_spec} />}
              {r.aroma_profile && <Spec k="Aroma profile" v={r.aroma_profile} />}
              {r.flavor_profile && <Spec k="Flavor profile" v={r.flavor_profile} />}
              {r.color && <Spec k="Color" v={r.color} />}
            </dl>
          </section>
        )}

        {/* Trust strip */}
        <section className="bg-white border-x border-b border-stone-200 px-6 py-4 flex items-center gap-2 text-[12px] text-stone-600">
          <ShieldCheck size={16} weight="duotone" className="text-emerald-800 shrink-0" />
          <span>
            Calculations rounded per <b>FDA 21 CFR 101.9</b>. Allergens declared per
            FALCPA + FASTER Act. This document is informational — verify
            commercial-batch outputs with an accredited laboratory.
          </span>
        </section>

        {/* "Made with Alaine's Bench" acquisition footer */}
        <footer className="bg-stone-900 text-stone-200 rounded-b-lg px-6 py-5 mt-0 flex flex-wrap items-center justify-between gap-3 print:rounded-none" data-testid="public-spec-footer">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-md bg-emerald-700 text-white grid place-items-center font-bold text-lg">A</div>
            <div>
              <div className="text-sm font-semibold text-white">Made with Alaine's Bench™</div>
              <div className="text-[11px] text-stone-400">FDA formulation studio for food scientists.</div>
            </div>
          </div>
          <Link
            to="/"
            className="text-[13px] font-medium px-4 py-2 rounded-md bg-emerald-700 hover:bg-emerald-600 text-white transition-colors"
            data-testid="public-spec-cta-link"
          >
            Try Alaine's Bench →
          </Link>
        </footer>
      </div>
    </div>
  );
}

function Row({ label, value, dv, bold, indent, indent2, italic }) {
  const pad = indent2 ? "pl-8" : indent ? "pl-4" : "";
  return (
    <div className={`flex justify-between text-[12px] py-0.5 border-b border-stone-300 ${pad}`}>
      <span className={`${bold ? "font-bold" : ""} ${italic ? "italic" : ""}`}>{label} <span className="font-normal">{value}</span></span>
      <span className="font-bold">{dv != null ? `${dv}%` : ""}</span>
    </div>
  );
}

function Spec({ k, v }) {
  return (
    <div className="flex flex-col">
      <dt className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold">{k}</dt>
      <dd className="text-stone-800">{v}</dd>
    </div>
  );
}

/**
 * Idempotently upsert a <meta> tag into <head>. `property` switch is needed
 * because Open Graph uses property="og:*" while standard meta uses name="*".
 */
function setMeta(key, value, isProperty = false) {
  const attr = isProperty || key.startsWith("og:") ? "property" : "name";
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
}
